import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  viewChild,
  output,
} from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MessageApiService, MessageThread, MessageThreadFilter, TaskUnreadClearedEvent } from './message.api.service';
import { TaskStatisStore } from './components/task-message-list/task-statis.store';
import { NoticeListComponent } from './components/notice-list/notice-list.component';
import { TaskMessageListComponent } from './components/task-message-list/task-message-list.component';
import { TaskMessageItemComponent } from './components/task-message-item/task-message-item.component';
import { SystemMessageItemComponent } from './components/system-message-item/system-message-item.component';
import { FlIconComponent } from '@common';

/**
 * 消息抽屉（容器内浮层）
 *
 * 放在 sidebar 右侧内容区内，遮罩与面板都被限制在该区域，天然不遮挡 sidebar。
 * 面板占容器宽 90%，从左侧滑入。由父组件 @if 条件渲染，存在即可见。
 */
@Component({
  selector: 'fl-message-panel',
  templateUrl: './message-panel.component.html',
  styleUrls: ['./message-panel.component.scss'],
  standalone: true,
  providers: [TaskStatisStore],
  imports: [
    CommonModule,
    NzButtonModule,
    NzBadgeModule,
    NzTooltipModule,
    NzSpinModule,
    NzSegmentedModule,
    FormsModule,
    NoticeListComponent,
    TaskMessageListComponent,
    TaskMessageItemComponent,
    SystemMessageItemComponent,
    FlIconComponent,
  ],
})
export class MessagePanelComponent implements OnInit, OnDestroy {
  /** 请求关闭（点遮罩 / Esc / 按钮） */
  readonly close = output<void>();

  /** 是否需要为左侧固定导航栏预留空间 */
  // TODO: Skipped for migration because:
  //  This input is used in combination with `@HostBinding` and migrating would
  //  break.
  @HostBinding('class.with-sidebar')
  @Input()
  hasSidebar = false;

  /** 消息聚合列表（后端 getMessageThreads 分页累积，系统消息 type===0 由后端返回在首项） */
  threads: MessageThread[] = [];

  /** 左栏筛选：全部 / 仅未读任务 */
  threadFilter: MessageThreadFilter = MessageThreadFilter.All;

  /** 供模板引用筛选枚举 */
  protected readonly MessageThreadFilter = MessageThreadFilter;

  /** 系统消息未读数（源自全局 unread$ 的 unread 字段；打开系统通知消费后自动归零） */
  systemUnread = 0;

  /** 当前已加载任务页 */
  private threadPage = 1;

  /** 已加载到的任务总数（排除静态系统消息条） */
  private threadCount = 0;

  /** 是否已加载完所有任务（供模板底部「没有更多了」判断） */
  threadReachedBottom = false;

  /** 任务列表加载中 */
  threadsLoading = false;

  /** 左栏滚动容器引用（用于检测列表是否撑满容器触发自动翻页） */
  private readonly threadScrollEl = viewChild<ElementRef<HTMLElement>>('threadScroll');

  /** 当前展开的任务 id（一次只展开一个；null 表示都没展开） */
  expandedTaskId: number | null = null;

  /** 右栏当前展示的任务 id（0 表示系统消息） */
  activeTaskId: number | null = null;

  /** 右栏当前选中的题 thread_id（TaskStatisItem.id；0 表示系统消息时无题） */
  activeThreadId: number | null = null;

  /** 最近一次某叶子 thread 已读事件，传给左栏对应 item 扣减其 statis */
  unclearedEvent?: TaskUnreadClearedEvent;

  /** 打开时的未读基线，service 轮询发现超出则提示新消息 */
  private baseCount = 0;

  /** 是否提示"有新消息"，列表第一项上方显示黄色横幅 */
  hasNewMessage = false;

  /** 刷新序号：每次点刷新 +1，驱动左栏重拉 statis（写入共享 store）后右栏随之联动 */
  private refreshTick = 0;

  /** 未读轮询订阅句柄 */
  private unreadSub: Subscription | null = null;

  private messageApiSrv = inject(MessageApiService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  ngOnInit() {
    const current = this.messageApiSrv.unread$.value;
    this.baseCount = current.total ?? 0;
    this.systemUnread = current.unread ?? 0;
    this.unreadSub = this.messageApiSrv.unread$.subscribe(summary => {
      // 系统消息未读随全局摘要联动（consumeSystemUnread 清零 unread 后此处归零）
      this.systemUnread = summary.unread ?? 0;
      if ((summary.total ?? 0) > this.baseCount) {
        this.hasNewMessage = true;
      }
      // OnPush：全局未读摘要的轮询订阅回调非模板事件，未读角标/新消息横幅需主动标记
      this.cdr.markForCheck();
    });
    // 面板打开期间锁定页面滚动，避免长页面在面板外滚动
    document.body.style.overflow = 'hidden';
    this.loadThreads();
  }

  ngOnDestroy() {
    document.body.style.overflow = '';
    this.unreadSub?.unsubscribe();
    this.unreadSub = null;
  }

  /**
   * 加载"还有未读"时刷新列表并加载第一页（用于刷新清单）
   *
   * 不清空旧列表，等第一页新数据到位后再整体替换，避免刷新时列表闪现空白。
   */
  private async loadThreads() {
    this.threadPage = 1;
    this.threadCount = 0;
    this.threadReachedBottom = false;
    this.threadsLoading = true;
    try {
      const result = await this.messageApiSrv.getMessageThreads(1, this.threadFilter);
      this.threads = this.filterSystemThread(result.results);
      this.threadCount = result.count;
      this.threadPage = 2;
      // 用后端 next 字段判定是否还有下一页（首页条数少时避免误判到底）
      this.threadReachedBottom = !result.next;
    } finally {
      this.threadsLoading = false;
    }
    // OnPush：await 续体非模板事件，消息列表/加载态需主动标记
    this.cdr.markForCheck();
    // 若列表不足一屏且还有下一页，自动补满，消除「无滚动条 → 无法触发滚动加载」的死角
    this.fillViewportIfNeeded();
  }

  /**
   * 加载下一页任务聚合到列表末尾（滚动到底触发）
   */
  private async loadNextThreads() {
    if (this.threadsLoading || this.threadReachedBottom) return;
    this.threadsLoading = true;
    try {
      const result = await this.messageApiSrv.getMessageThreads(this.threadPage, this.threadFilter);
      const existing = new Set(this.threads.map(t => t.taskId));
      const fresh = this.filterSystemThread(result.results.filter(t => !existing.has(t.taskId)));
      this.threads = this.threads.concat(fresh);
      this.threadCount = result.count;
      this.threadPage += 1;
      // next 不存在或无新增条数视为到底，避免异常分页下无限请求
      this.threadReachedBottom = !result.next || fresh.length === 0;
    } finally {
      this.threadsLoading = false;
    }
    // OnPush：await 续体非模板事件，追加的消息列表/加载态需主动标记
    this.cdr.markForCheck();
    this.fillViewportIfNeeded();
  }

  /**
   * 列表不足一屏且还有下一页时自动翻页，直到撑满或到底。
   * 解决「列表撑不满容器 → 无滚动条 → 无法触发滚动加载」的死角。
   * 放在 requestAnimationFrame 里等 Angular 渲染出新列表高度后再量。
   */
  private fillViewportIfNeeded() {
    if (this.threadReachedBottom || this.threadsLoading) return;
    const el = this.threadScrollEl()?.nativeElement;
    if (!el) return;
    requestAnimationFrame(() => {
      if (this.threadReachedBottom || this.threadsLoading) return;
      if (el.scrollHeight <= el.clientHeight) {
        this.loadNextThreads();
      }
    });
  }

  /**
   * 左栏任务列表滚动到底时加载下一页
   */
  onThreadScroll(event: Event) {
    const el = event.target as HTMLElement;
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (gap < 40) {
      this.loadNextThreads();
    }
  }

  /**
   * 切换左栏筛选
   *
   * 旧列表与选中项属于另一筛选口径，先清空再拉取，避免右侧继续展示已不在列表中的任务。
   */
  onThreadFilterChange(filter: MessageThreadFilter) {
    this.threadFilter = filter;
    this.threads = [];
    this.expandedTaskId = null;
    this.activeTaskId = null;
    this.activeThreadId = null;
    this.loadThreads();
  }

  /**
   * 「未读」筛选下系统消息没有未读数时隐藏
   *
   * 系统消息是后端固定混入的占位项，不属于任务，未读数为 0 时不应出现在未读结果里。
   */
  private filterSystemThread(list: MessageThread[]): MessageThread[] {
    if (this.threadFilter !== MessageThreadFilter.Unread || this.systemUnread > 0) return list;
    return list.filter(thread => thread.type !== 0);
  }

  /**
   * 点击横幅：刷新列表并清除提示
   */
  refreshThreads() {
    // 刷新列表并重建基线，横幅消失
    this.baseCount = this.messageApiSrv.unread$.value.total ?? 0;
    this.hasNewMessage = false;
    this.refreshTick += 1;
    this.loadThreads();
  }

  /** 点击任务聚合消息：系统消息直接进右栏通知列表；任务项切换手风琴展开并进右栏 */
  toggleTaskThread(thread: MessageThread) {
    // 系统消息不参与手风琴，直接在右栏展示系统通知（type===0 由后端返回）
    if (thread.type === 0) {
      this.expandedTaskId = null;
      this.activeTaskId = -1;
      this.activeThreadId = null;
      return;
    }
    // 点击已展开的任务 → 折叠
    if (this.expandedTaskId === thread.taskId) {
      this.expandedTaskId = null;
      this.activeTaskId = null;
      this.activeThreadId = null;
      return;
    }
    // 展开该任务，右栏默认第一题（未明确选题，activeThreadId 为 null）
    this.expandedTaskId = thread.taskId;
    this.activeTaskId = thread.taskId;
    this.activeThreadId = null;
  }

  /** 点击展开的某题号行：右栏切到对应题 */
  onSelectTaskQuestion(thread: MessageThread, threadId: number) {
    this.activeTaskId = thread.taskId;
    this.activeThreadId = threadId;
  }

  /** 左栏展开任务首拉 statis 后上报默认定位的题：仅当该任务仍是当前选中任务时应用，驱动右栏跳转 */
  onTaskDefaultQuestion(taskId: number, threadId: number) {
    if (this.activeTaskId === taskId) {
      this.activeThreadId = threadId;
    }
  }

  /** 当前是否选中系统消息（选中系统消息时 activeTaskId 置 -1，空态 null 表示未选中任何项） */
  get isSystemActive(): boolean {
    return this.activeTaskId === -1;
  }

  /** 子 thread 已读后，只扣减所属任务的聚合未读数；同时传给左栏对应 item 扣减其 statis 未读数 */
  onTaskUnreadCleared(event: TaskUnreadClearedEvent) {
    this.threads = this.threads.map(thread =>
      thread.taskId === event.taskId
        ? { ...thread, unReadCount: Math.max(0, thread.unReadCount - event.clearedCount) }
        : thread
    );
    this.unclearedEvent = event;
  }

  /**
   * Esc 关闭
   */
  @HostListener('window:keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.closeDrawer();
    }
  }

  closeDrawer() {
    this.close.emit();
  }
}
